import { Column, ColumnDef, RowData } from "@tanstack/react-table";

// 默认宽度常量
const DEFAULT_COLUMN_WIDTH = 150;

// 生成左侧阴影类名（提示右侧有内容）
const generateLeftShadowClass = (customClass?: string) => customClass || "";

// 生成右侧阴影类名（提示左侧有内容）
const generateRightShadowClass = (customClass?: string) =>
  customClass ||
  "after:content-[''] after:absolute after:top-0 after:right-0 after:w-[1px] after:h-full after:bg-border_white_transparent10 after:z-[10] after:pointer-events-none";

// 生成容器阴影类名（兼容旧版逻辑）
export const generateContainerShadowClass = (customClass?: string) =>
  customClass ||
  "absolute bottom-0 right-0 w-[46px] h-full pointer-events-none z-[7] bg-gradient-to-l from-background_solid_secondary to-transparent";

// 列位置和阴影类名计算工具函数（合并版本）
export const calculateColumnShadowClasses = <T extends RowData>(
  columnId: string,
  columnIndex: number,
  isPinnedLeft: boolean,
  isPinnedRight: boolean,
  leftPinnedColumns: ColumnDef<T, unknown>[],
  rightPinnedColumns: ColumnDef<T, unknown>[],
  allColumns: ColumnDef<T, unknown>[],
  isScrolledFromStart: boolean,
  isScrolledToEnd: boolean,
  maskClasses?: { cellLeft?: string; cellRight?: string },
) => {
  // 判断是否为左侧固定列的最后一个
  const isLastLeftPinned =
    isPinnedLeft &&
    leftPinnedColumns.length > 0 &&
    leftPinnedColumns[leftPinnedColumns.length - 1]?.id === columnId;

  // 判断是否为右侧固定列的第一个
  const isFirstRightPinned =
    isPinnedRight &&
    rightPinnedColumns.length > 0 &&
    rightPinnedColumns[0]?.id === columnId;

  // 判断是否为最后一个非固定列（当无右侧固定列时）
  const nonPinnedColumnsCount =
    allColumns.length - leftPinnedColumns.length - rightPinnedColumns.length;
  const isLastScrollableColumn =
    !isPinnedLeft &&
    !isPinnedRight &&
    columnIndex === leftPinnedColumns.length + nonPinnedColumnsCount - 1;

  // 右侧固定列第一个：左侧渐变阴影（提示右边有内容）
  const leftShadowClass =
    (isFirstRightPinned ||
      (isLastScrollableColumn && rightPinnedColumns.length === 0)) &&
    !isScrolledToEnd
      ? generateLeftShadowClass(maskClasses?.cellRight)
      : "";

  // 左侧固定列最后一个：右侧渐变阴影（提示左边有内容）
  const rightShadowClass =
    isLastLeftPinned && isScrolledFromStart
      ? generateRightShadowClass(maskClasses?.cellLeft)
      : "";

  return {
    // 列位置信息
    isLastLeftPinned,
    isFirstRightPinned,
    isLastScrollableColumn,
    // 阴影类名
    leftShadowClass,
    rightShadowClass,
  };
};

// 工具函数：获取列宽度
export const getColumnWidth = <T extends RowData>(
  column: Column<T, unknown>,
) => {
  const width = column.columnDef.meta?.width;
  const isPinned =
    column.columnDef.meta?.pinned === "left" ||
    column.columnDef.meta?.pinned === "right";

  if (typeof width === "number") {
    return width;
  }
  if (width) {
    return width;
  }
  return isPinned ? DEFAULT_COLUMN_WIDTH : undefined;
};

// 计算表格总宽度
export const calculateTotalWidth = <T extends RowData>(
  columns: ColumnDef<T, unknown>[],
): number => {
  return columns.reduce((sum: number, col) => {
    const width = col.meta?.width;
    const isPinned = col.meta?.pinned === "left";

    if (typeof width === "number") {
      return sum + width;
    }
    if (isPinned) {
      return sum + DEFAULT_COLUMN_WIDTH;
    }
    return sum;
  }, 0);
};

// 计算左侧固定列偏移量
export const calculateLeftOffset = <T extends RowData>(
  columnIndex: number,
  isPinned: boolean,
  leftPinnedColumns: ColumnDef<T, unknown>[],
  allColumns: ColumnDef<T, unknown>[],
): number => {
  if (!isPinned) {
    return 0;
  }

  const currentColumn = allColumns[columnIndex];
  const pinnedIndex = leftPinnedColumns.findIndex(
    (col) => col.id === currentColumn.id,
  );

  if (pinnedIndex <= 0) {
    return 0;
  }

  return leftPinnedColumns
    .slice(0, pinnedIndex)
    .reduce((sum: number, col) => sum + getColumnWidthForOffset(col), 0);
};

// 计算右侧固定列偏移量
export const calculateRightOffset = <T extends RowData>(
  columnIndex: number,
  isPinned: boolean,
  rightPinnedColumns: ColumnDef<T, unknown>[],
  allColumns: ColumnDef<T, unknown>[],
): number => {
  if (!isPinned) {
    return 0;
  }

  const currentColumn = allColumns[columnIndex];
  const pinnedIndex = rightPinnedColumns.findIndex(
    (col) => col.id === currentColumn.id,
  );

  if (pinnedIndex < 0) {
    return 0;
  }

  // 计算从当前列到表格右边缘的所有右侧固定列的宽度
  const columnsAfter = rightPinnedColumns.slice(pinnedIndex + 1);
  return columnsAfter.reduce(
    (sum: number, col) => sum + getColumnWidthForOffset(col),
    0,
  );
};

// 计算列宽度的通用函数
const getColumnWidthForOffset = (
  col: Column<any, unknown> | ColumnDef<any, unknown>,
): number => {
  let width: string | number | undefined;

  if ("columnDef" in col) {
    // Column 类型
    width = col.columnDef.meta?.width;
  } else {
    // ColumnDef 类型
    width = col.meta?.width;
  }

  if (typeof width === "number") {
    return width;
  }
  if (typeof width === "string") {
    const parsed = parseInt(width.replace(/[^0-9]/g, ""), 10);
    return isNaN(parsed) ? DEFAULT_COLUMN_WIDTH : parsed;
  }
  return DEFAULT_COLUMN_WIDTH;
};
